import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import {
  DataList,
  PageHeader,
  RowActions,
  StatusPill,
  useActionRunner,
  type DataListColumn,
} from "@ozpanel/kit";
import { useAuth } from "@ozpanel/ui";
import { Plus } from "lucide-react";
import { Button } from "@ozpanel/ui";
import { useCreate } from "../../useCreate";
import { useAccountsApi, type Tenant } from "../../accountsApi";
import { tenantActions } from "./tenantActions";

const columns: DataListColumn<Tenant>[] = [
  { key: "name", label: "Name", sortable: true },
  { key: "contact_email", label: "Contact", sortable: true },
  { key: "system_user", label: "System user", value: (t) => t.system_user || "—" },
  {
    key: "status",
    label: "Status",
    sortable: true,
    value: (t) => t.status,
    render: (t) => <StatusPill tone={t.status === "active" ? "ok" : "warn"}>{t.status}</StatusPill>,
  },
  { key: "cluster_mode", label: "Cluster mode", sortable: true },
];

export function TenantsScreen() {
  const api = useAccountsApi();
  const tenants = useQuery({ queryKey: ["tenants"], queryFn: api.listTenants });
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const runner = useActionRunner<Tenant>();
  const actions = tenantActions(api, { loadTeardown: api.teardownPreview, onImpersonated: () => void refresh() });
  const plans = useQuery({ queryKey: ["plans"], queryFn: api.listPlans, retry: false });
  const create = useCreate({
    title: "New account",
    description:
      "Creates the tenant and allocates its system user. Domains, mail and databases are added afterwards.",
    submitLabel: "Create account",
    fields: [
      { key: "name", label: "Account name", required: true, placeholder: "acme-corp" },
      { key: "contact_email", label: "Contact email", placeholder: "ops@acme.test" },
      { key: "contact_name", label: "Contact name" },
      {
        key: "plan_id",
        label: "Plan",
        required: true,
        // The backend refuses a tenant with no plan ("every limit unlimited, which is never
        // what anyone meant"), so this is required here rather than discovered as a 400.
        help: "Sets the account's limits. An account without a plan has no limits at all.",
        options: (plans.data?.items ?? []).map((pl) => ({ value: String(pl.id), label: pl.name })),
      },
      {
        key: "send_welcome",
        label: "Email the welcome to the first login",
        type: "bool",
        help: "The console address, the login, a one-use link to choose the password (7 days), and each domain's website, mail and DNS. No password is in it.",
      },
    ],
    run: (v) => api.createTenant({ ...v, plan_id: Number(v.plan_id) }),
    invalidates: [["tenants"]],
    success: (v) => `Account ${v.name} created`,
  });

  return (
    <div>
      <PageHeader
        title="Tenants"
        subtitle={tenants.data ? `${tenants.data.total} accounts` : undefined}
        actions={
          <Button variant="accent" onClick={create.open}>
            <Plus size={14} />
            New account
          </Button>
        }
      />
      <DataList
        items={tenants.data?.items ?? []}
        columns={columns}
        getRowKey={(t) => String(t.id)}
        onRowClick={(t) => navigate(`/tenants/${t.id}`)}
        rowActions={(t) => <RowActions actions={actions} obj={t} runner={runner} />}
        searchable
        searchPlaceholder="Search tenants…" loading={tenants.isLoading}
        error={tenants.error}
        errorContext="tenants" emptyMessage="No tenants yet." />
      {runner.dialog}
      {create.dialog}
    </div>
  );
}
